import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, ChevronRight, EyeOff, ImageOff, Search, X } from "lucide-react";
import { useListingsPagination } from "../hooks/useListingsPagination";
import { isPubliclyVisible } from "../utils/listingsQuery";
import Pagination from "./Pagination";
import ViewModeToggle, { useViewMode } from "./ViewModeToggle";

const PAGE_SIZE = 24;

function formatPrice(value) {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  return n.toLocaleString();
}

function pickDisplayPrice(d) {
  const map = [
    ["totalWebsitePrice", "Sale"],
    ["websitePrice", "Sale"],
    ["total_price", "Sale"],
    ["selling_price", "Sale"],
    ["leaseRate", "Lease/mo"],
  ];
  for (const [field, label] of map) {
    const formatted = formatPrice(d[field]);
    if (formatted) return `${label}: ${formatted}`;
  }
  return null;
}

function pickHero(d) {
  return d.primaryPhotoUrl || d.coverPhoto || d.photoUrl || null;
}

export default function ListingsManager() {
  // URL search params are the source of truth for filter + committed
  // search. That way clicking a card → detail → browser-back returns
  // the visitor to the same filter/search combo (see the Back button
  // on PropertyListingDetail which uses navigate(-1)). It also makes
  // the URL shareable ("send me the Incomplete list") and refresh-safe.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "all"; // "all" | "hidden" | "new" | "incomplete"
  const activeSearch = searchParams.get("q") || "";

  // `searchInput` stays local — it's the draft text in the input. Only
  // the committed value (Search button click) is written to the URL.
  const [searchInput, setSearchInput] = useState(activeSearch);
  const [viewMode, setViewMode] = useViewMode("listings", "list");

  // Update one URL param without disturbing the others. Removing the
  // key when the value is the default ("all", empty search) keeps the
  // URL clean.
  const patchSearchParams = (patches) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patches)) {
      if (v === "" || v == null || v === "all") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: false });
  };

  const setFilter = (v) => patchSearchParams({ filter: v });

  const {
    docs,
    page,
    totalCount,
    totalPages,
    isLoading,
    isPaging,
    goToPage,
  } = useListingsPagination({
    pageSize: PAGE_SIZE,
    search: activeSearch,
    filter,
  });

  // Keep the input in sync with active state on external resets
  // (e.g., navigating back to the URL without ?q= after clearing).
  useEffect(() => {
    setSearchInput(activeSearch);
  }, [activeSearch]);

  const submitSearch = () => {
    const next = searchInput.trim();
    if (next === activeSearch) return;
    patchSearchParams({ q: next });
  };

  const clearSearch = () => {
    setSearchInput("");
    if (activeSearch) patchSearchParams({ q: "" });
  };

  const isDirty = searchInput.trim() !== activeSearch;

  return (
    <section className="mt-6">
      <header className="flex flex-col gap-3 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-leechiuBlue m-0">
              Website-eligible property listings
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isLoading
                ? "Loading…"
                : activeSearch
                ? `${totalCount.toLocaleString()} match "${activeSearch}" — includes hidden`
                : `${totalCount.toLocaleString()} eligible for /property-search — hidden ones tagged`}
            </p>
          </div>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>

        <form
          className="flex flex-col sm:flex-row gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submitSearch();
          }}
        >
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, address, type, or ID…"
              className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-leechiuOrange focus:border-transparent outline-none text-sm"
            />
            {searchInput ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
          <button
            type="submit"
            disabled={!isDirty}
            className="px-4 py-2 rounded-lg bg-leechiuBlue text-white text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Search
          </button>
        </form>

        <FilterChips value={filter} onChange={setFilter} />

        {activeSearch ? (
          <div className="text-xs text-gray-500">
            Searching:{" "}
            <span className="font-medium text-leechiuBlue">
              "{activeSearch}"
            </span>
          </div>
        ) : null}
      </header>

      {isLoading ? (
        <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden"
            >
              <div className="aspect-[4/3] bg-gray-100 animate-pulse" />
              <div className="p-4 space-y-2">
                <div className="h-4 w-3/4 bg-gray-100 animate-pulse rounded" />
                <div className="h-3 w-1/2 bg-gray-100 animate-pulse rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : docs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-leechiuBlue/5 text-leechiuBlue flex items-center justify-center mb-3 mx-auto">
            <Building2 className="w-5 h-5" />
          </div>
          <h4 className="text-sm font-semibold text-leechiuBlue m-0">
            {activeSearch ? "No matches" : "No listings yet"}
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            {activeSearch
              ? `Nothing matches "${activeSearch}". Search looks across name, address, type, and ID — try a different keyword.`
              : "Property listings will appear here once they're added in the CRM."}
          </p>
          {activeSearch ? (
            <button
              type="button"
              onClick={clearSearch}
              className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-leechiuBlue text-white text-sm font-medium hover:opacity-90"
            >
              Clear search
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div
            className={`${
              viewMode === "card"
                ? "grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                : "flex flex-col gap-2"
            } transition-opacity ${isPaging ? "opacity-60" : "opacity-100"}`}
          >
            {docs.map((d) => {
              const title =
                d.property_name || d.website_title || "Untitled listing";
              const price = pickDisplayPrice(d);
              const hidden = !isPubliclyVisible(d);

              if (viewMode === "list") {
                // List: compact horizontal row. Title comes FIRST so
                // every row's title starts at the same left edge
                // (regardless of pill count). Pills flow right of the
                // title on the same line and wrap if the row is narrow.
                // No <img> means no Storage download per row.
                return (
                  <Link
                    key={d.id}
                    to={`/cms/property-search/${d.id}`}
                    className="group bg-white rounded-xl ring-1 ring-gray-100 hover:ring-leechiuBlue/20 hover:shadow-md transition-all flex items-center gap-4 pl-4 pr-2 py-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-leechiuBlue truncate m-0">
                          {title}
                        </h4>
                        {d.listing_type ? (
                          <span className="text-[10px] font-medium text-leechiuBlue bg-leechiuBlue/5 px-1.5 py-0.5 rounded-full ring-1 ring-leechiuBlue/10">
                            {d.listing_type}
                          </span>
                        ) : null}
                        {hidden ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-amber-200">
                            <EyeOff className="w-3 h-3" />
                            Hidden
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">
                        {d.address || "—"}
                        {price ? (
                          <>
                            <span className="mx-1.5 text-gray-300">·</span>
                            <span className="text-gray-700 cms-tabular">
                              {price}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-leechiuBlue shrink-0" />
                  </Link>
                );
              }

              // Card mode: thumbnail-first grid tile. Image bytes ARE
              // fetched only in this branch.
              const hero = pickHero(d);
              return (
                <Link
                  key={d.id}
                  to={`/cms/property-search/${d.id}`}
                  className="group bg-white rounded-2xl ring-1 ring-gray-100 hover:ring-leechiuBlue/20 hover:shadow-md transition-all overflow-hidden flex flex-col"
                >
                  <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                    {hero ? (
                      <img
                        src={hero}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-300">
                        <ImageOff className="w-8 h-8" />
                      </div>
                    )}
                    {hidden ? (
                      <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-amber-200">
                        <EyeOff className="w-3 h-3" />
                        Hidden
                      </span>
                    ) : null}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      {d.listing_type ? (
                        <span className="text-[10px] font-medium text-leechiuBlue bg-leechiuBlue/5 px-1.5 py-0.5 rounded-full ring-1 ring-leechiuBlue/10">
                          {d.listing_type}
                        </span>
                      ) : null}
                    </div>
                    <h4 className="text-sm font-semibold text-leechiuBlue m-0 line-clamp-2">
                      {title}
                    </h4>
                    {d.address ? (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {d.address}
                      </p>
                    ) : null}
                    {price ? (
                      <p className="text-xs text-gray-700 font-medium mt-2 cms-tabular">
                        {price}
                      </p>
                    ) : null}
                    <div className="flex-1" />
                    <div className="flex items-center justify-end mt-3 text-[11px] text-gray-400 group-hover:text-leechiuBlue transition-colors">
                      View details
                      <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            isPaging={isPaging}
            onChange={goToPage}
          />
        </>
      )}
    </section>
  );
}

// Single-select filter pills. "New" targets listings never opened via
// this CMS (checked by absence of the `cmsOpenedAt` timestamp that
// PropertyListingDetail stamps on first load). "Incomplete" surfaces
// listings that would render broken on the public site (missing
// title, price, photo, wrong pipeline stage, etc.) — the same checks
// the IncompleteWarning banner on the detail page runs.
const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "hidden", label: "Hidden only" },
  { value: "new", label: "New (not opened yet)" },
  { value: "incomplete", label: "Incomplete" },
];

function FilterChips({ value, onChange }) {
  return (
    <div className="inline-flex flex-wrap gap-1.5 p-1 rounded-lg bg-gray-100 self-start">
      {FILTER_OPTIONS.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              active
                ? "bg-white text-leechiuBlue shadow-sm ring-1 ring-gray-200"
                : "text-gray-600 hover:text-leechiuBlue"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
